import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import type { ReactElement } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";

export interface NavTab {
  label: string;
  /** The tab's route; the tab is active on it and on anything under it, unless
   *  another tab's route is closer (`/workflows` vs `/workflows/signals`). */
  to: string;
  icon?: ReactElement;
}

interface NavTabsProps {
  tabs: NavTab[];
  /** Names the tab strip for screen readers ("Account sections"). */
  label: string;
}

/**
 * Sibling pages of one section as tabs, each its own route, so every tab can
 * be linked to and survives a reload. Goes under the section's `PageHeader`;
 * tabs that only filter a list stay plain `Tabs`.
 */
export default function NavTabs({ tabs, label }: NavTabsProps) {
  const { pathname } = useLocation();
  const active = tabs
    .filter((t) => matchPath({ path: t.to, end: false }, pathname))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return (
    <Tabs
      value={active?.to ?? false}
      aria-label={label}
      variant="scrollable"
      scrollButtons={false}
      sx={{ mb: 3, borderBottom: 1, borderColor: "divider", minHeight: 40 }}
    >
      {tabs.map((t) => (
        <Tab
          key={t.to}
          value={t.to}
          label={t.label}
          icon={t.icon}
          iconPosition="start"
          component={Link}
          to={t.to}
          sx={{ minHeight: 40, py: 1, "& svg": { fontSize: 16 } }}
        />
      ))}
    </Tabs>
  );
}
